import { useEffect } from 'react'
import { exitsGroup, pickInDirection, rememberedIndex, stepInGrid, stepInGroup, type Box, type Direction, type GroupKind } from '../lib/focus'
import { STORAGE } from '../constants'

const DIRECTIONS: Record<number, Direction> = { 37: 'left', 38: 'up', 39: 'right', 40: 'down' }

/** Where focus was last left, per navigation group. Survives screen changes. */
function initialMemory() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE.focusMemory) || '{}') as Record<string, number>
    return new Map(Object.entries(value).filter((entry): entry is [string, number] => Number.isInteger(entry[1])).slice(-80))
  } catch { return new Map<string, number>() }
}
const memory = initialMemory()
export const rememberedNavIndex = (key: string) => memory.get(key)

function remember(key: string, index: number) {
  memory.set(key, index)
  try { localStorage.setItem(STORAGE.focusMemory, JSON.stringify(Object.fromEntries(Array.from(memory).slice(-80)))) } catch { /* private mode/quota */ }
}
/** The exact last control in each screen or modal layer. Group memory alone is
 * not enough: the first remembered row in DOM order may not be the row the
 * viewer actually left. */
const scopeMemory = new WeakMap<ParentNode, HTMLElement>()

const LAYER_SELECTOR = '.admin-overlay, .detail-overlay, .source-panel, .trailer-overlay, .player, .player-menu, .settings-dialog'

function boxOf(element: HTMLElement): Box {
  const rect = element.getBoundingClientRect()
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
}

function onScreen(element: HTMLElement) {
  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return false
  const style = window.getComputedStyle(element)
  if (style.visibility === 'hidden' || style.display === 'none' || style.pointerEvents === 'none') return false
  // a card scrolled out of its own row is laid out but unreachable
  const scroller = element.closest<HTMLElement>('[data-nav-group]')
  if (scroller && scroller.scrollWidth > scroller.clientWidth + 1) {
    const bounds = scroller.getBoundingClientRect()
    if (rect.right < bounds.left - 1 || rect.left > bounds.right + 1) return false
  }
  return true
}

/** The topmost modal layer owns navigation. */
function activeScope(): ParentNode {
  const layers = Array.from(document.querySelectorAll<HTMLElement>(LAYER_SELECTOR))
    .filter((element) => {
      const style = window.getComputedStyle(element)
      return style.visibility !== 'hidden' && style.display !== 'none'
    })
  return layers[layers.length - 1] || document
}

function scopeOf(element: HTMLElement): ParentNode {
  return element.closest<HTMLElement>(LAYER_SELECTOR) || document
}

function focusables(scope: ParentNode = activeScope()) {
  return Array.from(scope.querySelectorAll<HTMLElement>('[data-focusable]:not([disabled])')).filter(onScreen)
}

/** A TV browser can drop native focus after an app switch or Magic Remote
 * pointer interaction. The next D-pad press must restore a usable cursor
 * instead of leaving the viewer trapped on the current screen. */
function recoveryTarget(direction?: Direction) {
  const scope = activeScope()
  const available = focusables(scope)
  if (!available.length) return null
  if (scope === document && direction === 'up') {
    return available.find((element) => element.matches('.top-nav-item.active'))
      || available.find((element) => element.matches('.top-nav-item'))
      || available[0]
  }
  return available.find((element) => element.hasAttribute('data-autofocus')) || available[0]
}

/** A grid whose items each declare their visual cell can be navigated by cell
 * rather than by position in the focusable list, so blank cells do not shift
 * every key after them. Grids without declared cells keep the list behaviour. */
function declaredCells(kind: GroupKind, items: HTMLElement[]) {
  if (kind !== 'grid' || !items.length) return null
  const cells: number[] = []
  for (const item of items) {
    const raw = item.getAttribute('data-nav-index')
    const cell = raw === null ? Number.NaN : Number(raw)
    if (!Number.isInteger(cell) || cell < 0) return null
    cells.push(cell)
  }
  const occupied: boolean[] = new Array(Math.max(...cells) + 1).fill(false)
  const byCell: Array<HTMLElement | undefined> = []
  items.forEach((item, index) => {
    occupied[cells[index]] = true
    byCell[cells[index]] = item
  })
  return { cells, occupied, byCell }
}

function groupOf(element: HTMLElement) {
  const node = element.closest<HTMLElement>('[data-nav-group]')
  if (!node) return null
  const kind = (node.getAttribute('data-nav-group') || 'row') as GroupKind
  const key = node.getAttribute('data-nav-key') || node.className || 'group'
  const items = Array.from(node.querySelectorAll<HTMLElement>('[data-focusable]:not([disabled])')).filter(onScreen)
  const columns = Number(node.getAttribute('data-nav-columns')) || 1
  return { node, kind, key, items, columns, grid: declaredCells(kind, items) }
}

export function applyFocus(element?: HTMLElement | null) {
  if (!element) return
  const fixedLayers = Array.from(document.querySelectorAll<HTMLElement>('.admin-overlay, .detail-overlay, .source-panel, .trailer-overlay, .player, .settings-dialog'))
    .filter((layer) => layer.contains(element))
  document.querySelectorAll('[data-focused]').forEach((node) => node.removeAttribute('data-focused'))
  document.querySelectorAll('[data-active]').forEach((node) => node.removeAttribute('data-active'))
  element.setAttribute('data-focused', '')
  scopeMemory.set(scopeOf(element), element)

  const group = groupOf(element)
  if (group) {
    group.node.setAttribute('data-active', '')
    const declaredRaw = element.getAttribute('data-nav-index')
    const declared = declaredRaw === null ? Number.NaN : Number(declaredRaw)
    const index = Number.isInteger(declared) ? declared : group.items.indexOf(element)
    if (index >= 0) remember(group.key, index)
  }

  element.focus({ preventScroll: true })
  try {
    element.scrollIntoView({ block: 'nearest', inline: 'center' })
  } catch {
    element.scrollIntoView(false)
  }
  // overflow:hidden elements still scroll programmatically. Keep fixed modal
  // shells pinned while allowing their inner lists/detail scrollers to move.
  fixedLayers.forEach((layer) => { layer.scrollTop = 0; layer.scrollLeft = 0 })
}

/** Restore the remembered item for a group, or its first item. */
function enterGroup(node: HTMLElement, direction: Direction) {
  const kind = (node.getAttribute('data-nav-group') || 'row') as GroupKind
  const key = node.getAttribute('data-nav-key') || node.className || 'group'
  const items = Array.from(node.querySelectorAll<HTMLElement>('[data-focusable]:not([disabled])')).filter(onScreen)
  if (!items.length) return null
  if (kind === 'row' && (direction === 'left' || direction === 'right')) {
    return direction === 'right' ? items[0] : items[items.length - 1]
  }
  const remembered = memory.get(key)
  return items.find((item) => item.hasAttribute('data-nav-index') && Number(item.getAttribute('data-nav-index')) === remembered)
    || items[rememberedIndex(remembered, items.length)] || items[0]
}

function move(current: HTMLElement, direction: Direction) {
  // Top navigation and each screen declare one deliberate hand-off. This
  // prevents geometry from sending Movies to Settings merely because those
  // controls happen to line up on a wide screen.
  if (direction === 'down' && current.classList.contains('top-nav-item')) {
    const section = current.getAttribute('data-section')
    const entry = section ? document.querySelector<HTMLElement>(`[data-screen-entry="${section}"]`) : null
    if (entry && onScreen(entry)) return entry
  }
  if (direction === 'up') {
    const section = current.getAttribute('data-nav-up-section')
    const destination = section ? document.querySelector<HTMLElement>(`.top-nav-item[data-section="${section}"]`) : null
    if (destination) return destination
  }

  const group = groupOf(current)

  if (group) {
    const index = group.items.indexOf(current)
    if (group.grid && index >= 0) {
      const cell = stepInGrid(group.grid.occupied, group.grid.cells[index], direction, group.columns)
      const target = cell >= 0 ? group.grid.byCell[cell] : undefined
      if (target) return target
    } else {
      const next = stepInGroup(group.kind, direction, index, group.items.length, group.columns)
      if (next >= 0) return group.items[next]
    }
    if (!exitsGroup(group.kind, direction, true)) return null
  }

  // leaving the group: prefer entering the next group in that direction, at
  // the position it was left in, rather than whatever is geometrically nearest
  const scope = activeScope()
  const outside = focusables(scope).filter((element) => element !== current && (!group || !group.node.contains(element)))
  if (!outside.length) return null

  const chosen = pickInDirection(boxOf(current), outside.map(boxOf), direction)
  if (chosen < 0) return null
  const target = outside[chosen]
  const targetGroup = target.closest<HTMLElement>('[data-nav-group]')
  if (targetGroup && (!group || targetGroup !== group.node)) {
    return enterGroup(targetGroup, direction) || target
  }
  return target
}

export function useRemoteNavigation(focusKey: string) {
  // when the layer or screen changes, restore where focus was left; only fall
  // back to data-autofocus when there is nothing to restore
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const available = focusables()
      if (!available.length) return
      const active = document.activeElement as HTMLElement | null
      // webOS may natively focus the first DOM button (Home) before React's
      // first focus pass. Preserve only focus Vela already owns; otherwise the
      // screen's deliberate data-autofocus entry must win.
      if (active?.hasAttribute('data-focused') && available.indexOf(active) >= 0) {
        applyFocus(active)
        return
      }
      const exact = scopeMemory.get(activeScope())
      if (exact && available.indexOf(exact) >= 0) {
        applyFocus(exact)
        return
      }
      const restored = available.find((element) => {
        const group = groupOf(element)
        if (!group) return false
        const index = memory.get(group.key)
        if (index === undefined) return false
        const declared = group.items.find((item) => item.hasAttribute('data-nav-index') && Number(item.getAttribute('data-nav-index')) === index)
        return (declared || group.items[rememberedIndex(index, group.items.length)]) === element
      })
      applyFocus(restored || available.find((element) => element.hasAttribute('data-autofocus')) || available[0])
    }, 60)
    return () => clearTimeout(timer)
  }, [focusKey])

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const code = event.keyCode || event.which
      if (DIRECTIONS[code] || code === 13 || code === 461) document.documentElement.setAttribute('data-input-mode', 'dpad')
      if (code === 461 || event.key === 'Escape' || event.key === 'GoBack') {
        event.preventDefault()
        window.dispatchEvent(new CustomEvent('vela:back'))
        return
      }

      const direction = DIRECTIONS[code]
      const active = document.activeElement as HTMLElement | null

      if (!active?.matches('[data-focusable]')) {
        if (direction) {
          const recovered = recoveryTarget(direction)
          if (recovered) {
            event.preventDefault()
            applyFocus(recovered)
          }
        }
        return
      }

      if (direction) {
        // let a text field own left/right so the caret still works
        if ((active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') && (direction === 'left' || direction === 'right')) return
        // a focused control can claim the keys it needs (the scrubber does)
        const captured = active.getAttribute('data-nav-capture')
        if (captured && captured.split(' ').indexOf(direction) >= 0) return
        const row = active.closest<HTMLElement>('[data-nav-group="row"][data-virtual-count]')
        const virtualIndex = Number(active.getAttribute('data-nav-index'))
        const virtualCount = Number(row?.getAttribute('data-virtual-count'))
        if (row && Number.isInteger(virtualIndex) && Number.isInteger(virtualCount) && (direction === 'left' || direction === 'right')) {
          const targetIndex = virtualIndex + (direction === 'right' ? 1 : -1)
          const mounted = row.querySelector<HTMLElement>(`[data-nav-index="${targetIndex}"]`)
          if (targetIndex >= 0 && targetIndex < virtualCount && (!mounted || !onScreen(mounted))) {
            event.preventDefault()
            row.dispatchEvent(new CustomEvent('vela:virtual-focus', { detail: { index: targetIndex, direction } }))
            return
          }
        }
        const next = move(active, direction)
        if (next) {
          event.preventDefault()
          applyFocus(next)
        }
        return
      }

      if ((code === 13 || event.key === 'Enter') && active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA') {
        event.preventDefault()
        active.click()
      }
    }

    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])

  useEffect(() => {
    const focusFromPointer = (event: Event) => {
      const origin = event.target
      if (!(origin instanceof Element)) return
      const target = origin.closest<HTMLElement>('[data-focusable]:not([disabled])')
      if (!target || !onScreen(target)) return
      document.documentElement.setAttribute('data-input-mode', 'pointer')
      const scope = activeScope()
      if (scope !== document && !scope.contains(target)) return
      if (document.activeElement !== target || !target.hasAttribute('data-focused')) applyFocus(target)
    }
    const horizontalWheel = (event: WheelEvent) => {
      const origin = event.target
      if (!(origin instanceof Element) || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      const row = origin.closest<HTMLElement>('.media-row, .filter-scroll, .episode-list, .people-row')
      if (!row || row.scrollWidth <= row.clientWidth + 1) return
      event.preventDefault()
      row.scrollLeft += event.deltaY
    }
    // Older webOS exposes Magic Remote pointer movement as mouse events. Use
    // mousemove, not mouseover: scrolling a row under a stationary pointer
    // fires mouseover and used to steal D-pad focus back to an earlier card.
    document.addEventListener('mousemove', focusFromPointer, true)
    document.addEventListener('click', focusFromPointer, true)
    document.addEventListener('wheel', horizontalWheel, { capture: true, passive: false })
    return () => {
      document.removeEventListener('mousemove', focusFromPointer, true)
      document.removeEventListener('click', focusFromPointer, true)
      document.removeEventListener('wheel', horizontalWheel, true)
    }
  }, [])
}
